import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { ProtectedRoute } from '@/features/auth/ProtectedRoute';
import { AuthCallback } from '@/features/auth/AuthCallback';
import { AppShell } from '@/components/layout/AppShell';
import { OnboardingGuard } from '@/features/onboarding/OnboardingGuard';
/* ─── Lazy-loaded room pages (code-split per room) ───────── */
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));
const HealthPage = lazy(() => import('@/features/health/HealthPage'));
const WealthPage = lazy(() => import('@/features/wealth/WealthPage'));
const BusinessPage = lazy(() => import('@/features/business/BusinessPage'));
const JournalPage = lazy(() => import('@/features/journal/JournalPage'));
const GamePage = lazy(() => import('@/features/game/GamePage'));
const ToolsPage = lazy(() => import('@/features/tools/ToolsPage'));
const ContentPage = lazy(() => import('@/features/content/ContentPage'));
const SmartHomePage = lazy(() => import('@/features/smarthome/SmartHomePage'));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage'));
const OnboardingPage = lazy(() => import('@/features/onboarding/OnboardingPage'));
const PrivacyPolicy = lazy(() => import('@/pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('@/pages/TermsOfService'));
const AdminPanel = lazy(() => import('@/features/admin/AdminPanel'));

/**
 * App — root component with routing.
 */
function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
          <Routes>
            {/* Public legal pages (no auth) */}
            <Route
              path="/privacy"
              element={
                <Suspense fallback={<div className="p-6 text-sm text-[var(--muted-foreground)]">Loading…</div>}>
                  <PrivacyPolicy />
                </Suspense>
              }
            />
            <Route
              path="/terms"
              element={
                <Suspense fallback={<div className="p-6 text-sm text-[var(--muted-foreground)]">Loading…</div>}>
                  <TermsOfService />
                </Suspense>
              }
            />

            {/* Auth callback (no protection needed) */}
            <Route path="/auth/callback" element={<AuthCallback />} />

            {/* Protected app shell with nested routes */}
            <Route
              element={
                <ProtectedRoute>
                  <OnboardingGuard />
                </ProtectedRoute>
              }
            >
              <Route path="onboarding" element={<OnboardingPage />} />
              <Route element={<AppShell />}>
                <Route index element={<DashboardPage />} />
                <Route path="health" element={<HealthPage />} />
                <Route path="wealth" element={<WealthPage />} />
                <Route path="business" element={<BusinessPage />} />
                <Route path="journal" element={<JournalPage />} />
                <Route path="game" element={<GamePage />} />
                <Route path="tools" element={<ToolsPage />} />
                <Route path="content" element={<ContentPage />} />
                <Route path="smarthome" element={<SmartHomePage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="admin" element={<AdminPanel />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
    </AuthProvider>
  );
}

export { App };
